import React, { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  CircularProgress,
  Chip,
  Alert,
  TextField,
  Checkbox,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  AccountBalance as AccountIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import Modal from '../common/Modal';

const containerVariants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
};

const AssignUsersGroupsModal = ({
  account,
  accounts, // Support for multiple accounts
  users = [],
  groups = [],
  permissionSets = [],
  existingAssignments = [],
  onSubmit,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    principalType: 'USER',
    selectedPrincipals: [],
    permissionSetIds: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Search states
  const [permissionSetSearch, setPermissionSetSearch] = useState('');
  const [principalSearch, setPrincipalSearch] = useState('');

  // Support both single account and multiple accounts
  const targetAccounts = accounts || (account ? [account] : []);
  const isMultipleAccounts = targetAccounts.length > 1;

  const accountId = !isMultipleAccounts && targetAccounts[0] ? (targetAccounts[0].accountId || targetAccounts[0].account_id) : null;
  const accountName = !isMultipleAccounts && targetAccounts[0] ? (targetAccounts[0].accountName || targetAccounts[0].name || accountId) : null;

  // Filter available principals (exclude already assigned ones)
  const getAvailablePrincipals = () => {
    if (formData.principalType === 'USER') {
      return users.filter((user) => {
        // Check if already assigned with ALL selected permission sets
        if (formData.permissionSetIds.length === 0) return true;
        const existingPs = existingAssignments
          .filter((a) => a.principalType === 'USER' && a.username === user.username)
          .map((a) => a.permissionSetId);
        // Show user if they don't have all the selected permission sets
        return !formData.permissionSetIds.every((psId) => existingPs.includes(psId));
      });
    } else {
      return groups.filter((group) => {
        if (formData.permissionSetIds.length === 0) return true;
        const existingPs = existingAssignments
          .filter((a) => a.principalType === 'GROUP' && a.groupName === group.name)
          .map((a) => a.permissionSetId);
        return !formData.permissionSetIds.every((psId) => existingPs.includes(psId));
      });
    }
  };

  const availablePrincipals = getAvailablePrincipals();

  // Filtered permission sets based on search
  const filteredPermissionSets = useMemo(() => {
    if (!permissionSetSearch) return permissionSets;
    const query = permissionSetSearch.toLowerCase();
    return permissionSets.filter((ps) =>
      (ps.name || '').toLowerCase().includes(query) ||
      (ps.description || '').toLowerCase().includes(query)
    );
  }, [permissionSets, permissionSetSearch]);

  // Filtered principals based on search
  const filteredPrincipals = useMemo(() => {
    if (!principalSearch) return availablePrincipals;
    const query = principalSearch.toLowerCase();

    if (formData.principalType === 'USER') {
      return availablePrincipals.filter((user) =>
        (user.username || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query) ||
        (user.firstName || '').toLowerCase().includes(query) ||
        (user.lastName || '').toLowerCase().includes(query)
      );
    } else {
      return availablePrincipals.filter((group) =>
        (group.name || '').toLowerCase().includes(query) ||
        (group.description || '').toLowerCase().includes(query)
      );
    }
  }, [availablePrincipals, principalSearch, formData.principalType]);

  const handlePermissionSetToggle = (psId) => {
    setFormData((prev) => ({
      ...prev,
      permissionSetIds: prev.permissionSetIds.includes(psId)
        ? prev.permissionSetIds.filter((id) => id !== psId)
        : [...prev.permissionSetIds, psId],
    }));
    setError('');
  };

  const handlePrincipalToggle = (principalName) => {
    setFormData((prev) => ({
      ...prev,
      selectedPrincipals: prev.selectedPrincipals.includes(principalName)
        ? prev.selectedPrincipals.filter((p) => p !== principalName)
        : [...prev.selectedPrincipals, principalName],
    }));
    setError('');
  };

  const handleSelectAllPermissionSets = () => {
    const allIds = filteredPermissionSets.map((ps) => ps.id);
    const allSelected = allIds.every((id) => formData.permissionSetIds.includes(id));

    if (allSelected) {
      // Deselect all filtered
      setFormData((prev) => ({
        ...prev,
        permissionSetIds: prev.permissionSetIds.filter((id) => !allIds.includes(id)),
      }));
    } else {
      // Select all filtered
      setFormData((prev) => ({
        ...prev,
        permissionSetIds: [...new Set([...prev.permissionSetIds, ...allIds])],
      }));
    }
  };

  const handleSelectAllPrincipals = () => {
    const allNames = filteredPrincipals.map((p) =>
      formData.principalType === 'USER' ? p.username : p.name
    );
    const allSelected = allNames.every((name) => formData.selectedPrincipals.includes(name));

    if (allSelected) {
      setFormData((prev) => ({
        ...prev,
        selectedPrincipals: prev.selectedPrincipals.filter((p) => !allNames.includes(p)),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        selectedPrincipals: [...new Set([...prev.selectedPrincipals, ...allNames])],
      }));
    }
  };

  const handlePrincipalTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      principalType: type,
      selectedPrincipals: [],
    }));
    setPrincipalSearch('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.selectedPrincipals.length === 0) {
      setError('Please select at least one user or group');
      return;
    }

    if (formData.permissionSetIds.length === 0) {
      setError('Please select at least one permission set');
      return;
    }

    setLoading(true);

    try {
      // Get all account IDs
      const accountIds = targetAccounts.map(acc => acc.accountId || acc.account_id);

      // Create assignments for each principal with each permission set across all accounts
      let allSuccess = true;
      for (const principal of formData.selectedPrincipals) {
        for (const permissionSetId of formData.permissionSetIds) {
          const assignmentData = {
            permissionSetId,
            principalType: formData.principalType,
            accountId: accountIds,
          };

          if (formData.principalType === 'USER') {
            assignmentData.username = principal;
          } else {
            assignmentData.groupName = principal;
          }

          const success = await onSubmit(assignmentData);
          if (!success) {
            allSuccess = false;
          }
        }
      }

      if (allSuccess) {
        onClose();
      }
    } catch (err) {
      setError('Failed to create assignments');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#4698D3',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4698D3',
      },
    },
  };

  const isAllPermissionSetsSelected = filteredPermissionSets.length > 0 &&
    filteredPermissionSets.every((ps) => formData.permissionSetIds.includes(ps.id));

  const isPermissionSetsIndeterminate = formData.permissionSetIds.length > 0 &&
    filteredPermissionSets.some((ps) => formData.permissionSetIds.includes(ps.id)) &&
    !isAllPermissionSetsSelected;

  const isAllPrincipalsSelected = filteredPrincipals.length > 0 &&
    filteredPrincipals.every((p) =>
      formData.selectedPrincipals.includes(formData.principalType === 'USER' ? p.username : p.name)
    );

  const isPrincipalsIndeterminate = formData.selectedPrincipals.length > 0 &&
    filteredPrincipals.some((p) =>
      formData.selectedPrincipals.includes(formData.principalType === 'USER' ? p.username : p.name)
    ) &&
    !isAllPrincipalsSelected;

  return (
    <Modal onClose={onClose}>
      <motion.div
        variants={containerVariants}
        initial="initial"
        animate="animate"
        style={{ maxHeight: '90vh', overflowY: 'auto', width: 600 }}
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#EBF5FB',
            borderRadius: '12px 12px 0 0',
            p: 3,
            borderBottom: '1px solid #E5E5E5',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#4698D3',
                width: 48,
                height: 48,
              }}
            >
              <PersonAddIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.5rem',
                  color: '#253E66',
                }}
              >
                Assign users or groups
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                  fontWeight: 500,
                }}
              >
                {isMultipleAccounts
                  ? `Grant access to ${targetAccounts.length} accounts`
                  : `Grant access to ${accountName}`}
              </Typography>
            </Box>
          </Box>
        </Paper>
        </motion.div>

        {/* Form Content */}
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '0 0 12px 12px',
            p: 3,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* Account Display (Read-only) */}
            <motion.div variants={itemVariants}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <AccountIcon sx={{ color: '#F39C12', fontSize: 22 }} />
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#253E66',
                  }}
                >
                  {isMultipleAccounts ? `AWS Accounts (${targetAccounts.length})` : 'AWS Account'}
                </Typography>
              </Box>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  p: 2,
                  maxHeight: isMultipleAccounts ? '100px' : 'auto',
                  overflowY: isMultipleAccounts ? 'auto' : 'visible',
                }}
              >
                {isMultipleAccounts ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {targetAccounts.map((acc) => {
                      const accId = acc.accountId || acc.account_id;
                      const accName = acc.accountName || acc.name || accId;
                      return (
                        <Chip
                          key={accId}
                          icon={<AccountIcon sx={{ fontSize: 16 }} />}
                          label={accName}
                          size="small"
                          sx={{
                            backgroundColor: '#FFF8E1',
                            color: '#F39C12',
                            border: '1px solid #FFE082',
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                          }}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: '#253E66',
                      }}
                    >
                      {accountName}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: '#666666',
                      }}
                    >
                      {accountId}
                    </Typography>
                  </>
                )}
              </Paper>
            </Box>
            </motion.div>

            {/* Permission Set Selection with Search */}
            <motion.div variants={itemVariants}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SecurityIcon sx={{ color: '#27AE60', fontSize: 22 }} />
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#253E66',
                    }}
                  >
                    Permission Sets
                  </Typography>
                  {formData.permissionSetIds.length > 0 && (
                    <Chip
                      label={formData.permissionSetIds.length}
                      size="small"
                      sx={{
                        backgroundColor: '#F0F9F4',
                        color: '#27AE60',
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        height: 20,
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                </Box>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Search permission sets..."
                value={permissionSetSearch}
                onChange={(e) => setPermissionSetSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...inputStyle, mb: 1 }}
              />

              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  maxHeight: 180,
                  overflowY: 'auto',
                }}
              >
                {filteredPermissionSets.length > 0 && (
                  <Box
                    sx={{
                      borderBottom: '1px solid #E5E5E5',
                      px: 1,
                      py: 0.5,
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAllPermissionSetsSelected}
                          indeterminate={isPermissionSetsIndeterminate}
                          onChange={handleSelectAllPermissionSets}
                          sx={{
                            color: '#E5E5E5',
                            '&.Mui-checked': { color: '#27AE60' },
                            '&.MuiCheckbox-indeterminate': { color: '#27AE60' },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: '#666666',
                          }}
                        >
                          Select All
                        </Typography>
                      }
                    />
                  </Box>
                )}
                {filteredPermissionSets.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.85rem',
                        color: '#999999',
                        fontStyle: 'italic',
                      }}
                    >
                      {permissionSetSearch ? 'No permission sets match your search' : 'No permission sets available'}
                    </Typography>
                  </Box>
                ) : (
                  filteredPermissionSets.map((ps) => (
                    <Box
                      key={ps.id}
                      onClick={() => handlePermissionSetToggle(ps.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#FFFFFF',
                        },
                        borderBottom: '1px solid #F0F0F0',
                      }}
                    >
                      <Checkbox
                        checked={formData.permissionSetIds.includes(ps.id)}
                        sx={{
                          color: '#E5E5E5',
                          '&.Mui-checked': { color: '#27AE60' },
                        }}
                      />
                      <SecurityIcon sx={{ fontSize: 18, color: '#27AE60' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            color: '#253E66',
                          }}
                        >
                          {ps.name}
                        </Typography>
                        {ps.description && (
                          <Typography
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontSize: '0.75rem',
                              color: '#666666',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {ps.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Paper>
            </Box>
            </motion.div>

            {/* Principal Type Toggle */}
            <motion.div variants={itemVariants}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                {formData.principalType === 'GROUP' ? (
                  <GroupIcon sx={{ color: '#9B59B6', fontSize: 22 }} />
                ) : (
                  <PersonIcon sx={{ color: '#4698D3', fontSize: 22 }} />
                )}
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#253E66',
                  }}
                >
                  Assign To
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={formData.principalType === 'USER' ? 'contained' : 'outlined'}
                  startIcon={<PersonIcon />}
                  onClick={() => handlePrincipalTypeChange('USER')}
                  sx={{
                    flex: 1,
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    ...(formData.principalType === 'USER'
                      ? {
                          backgroundColor: '#4698D3',
                          '&:hover': { backgroundColor: '#3580B8' },
                        }
                      : {
                          borderColor: '#E5E5E5',
                          color: '#666666',
                          '&:hover': { borderColor: '#4698D3', color: '#4698D3' },
                        }),
                  }}
                >
                  Users
                </Button>
                <Button
                  variant={formData.principalType === 'GROUP' ? 'contained' : 'outlined'}
                  startIcon={<GroupIcon />}
                  onClick={() => handlePrincipalTypeChange('GROUP')}
                  sx={{
                    flex: 1,
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    ...(formData.principalType === 'GROUP'
                      ? {
                          backgroundColor: '#9B59B6',
                          '&:hover': { backgroundColor: '#8E44AD' },
                        }
                      : {
                          borderColor: '#E5E5E5',
                          color: '#666666',
                          '&:hover': { borderColor: '#9B59B6', color: '#9B59B6' },
                        }),
                  }}
                >
                  Groups
                </Button>
              </Box>
            </Box>
            </motion.div>

            {/* User/Group Selection with Search */}
            <motion.div variants={itemVariants}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                {formData.principalType === 'USER' ? (
                  <PersonIcon sx={{ color: '#4698D3', fontSize: 22 }} />
                ) : (
                  <GroupIcon sx={{ color: '#9B59B6', fontSize: 22 }} />
                )}
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#253E66',
                  }}
                >
                  {formData.principalType === 'USER' ? 'Select Users' : 'Select Groups'}
                </Typography>
                {formData.selectedPrincipals.length > 0 && (
                  <Chip
                    label={formData.selectedPrincipals.length}
                    size="small"
                    sx={{
                      backgroundColor: formData.principalType === 'USER' ? '#EBF5FB' : '#F5EBF8',
                      color: formData.principalType === 'USER' ? '#4698D3' : '#9B59B6',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      height: 20,
                      fontSize: '0.75rem',
                    }}
                  />
                )}
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder={formData.principalType === 'USER' ? 'Search users...' : 'Search groups...'}
                value={principalSearch}
                onChange={(e) => setPrincipalSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...inputStyle, mb: 1 }}
              />

              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {filteredPrincipals.length > 0 && (
                  <Box
                    sx={{
                      borderBottom: '1px solid #E5E5E5',
                      px: 1,
                      py: 0.5,
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAllPrincipalsSelected}
                          indeterminate={isPrincipalsIndeterminate}
                          onChange={handleSelectAllPrincipals}
                          sx={{
                            color: '#E5E5E5',
                            '&.Mui-checked': { color: formData.principalType === 'USER' ? '#4698D3' : '#9B59B6' },
                            '&.MuiCheckbox-indeterminate': { color: formData.principalType === 'USER' ? '#4698D3' : '#9B59B6' },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: '#666666',
                          }}
                        >
                          Select All
                        </Typography>
                      }
                    />
                  </Box>
                )}
                {filteredPrincipals.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.85rem',
                        color: '#999999',
                        fontStyle: 'italic',
                      }}
                    >
                      {principalSearch
                        ? `No ${formData.principalType === 'USER' ? 'users' : 'groups'} match your search`
                        : `No ${formData.principalType === 'USER' ? 'users' : 'groups'} available`}
                    </Typography>
                  </Box>
                ) : formData.principalType === 'USER' ? (
                  filteredPrincipals.map((user) => (
                    <Box
                      key={user.username}
                      onClick={() => handlePrincipalToggle(user.username)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#FFFFFF',
                        },
                        borderBottom: '1px solid #F0F0F0',
                      }}
                    >
                      <Checkbox
                        checked={formData.selectedPrincipals.includes(user.username)}
                        sx={{
                          color: '#E5E5E5',
                          '&.Mui-checked': { color: '#4698D3' },
                        }}
                      />
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: '#4698D3',
                          fontSize: '0.75rem',
                        }}
                      >
                        {(user.firstName?.[0] || user.username?.[0] || 'U').toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            color: '#253E66',
                          }}
                        >
                          {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.username}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontSize: '0.75rem',
                            color: '#666666',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {user.email || user.username}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  filteredPrincipals.map((group) => (
                    <Box
                      key={group.name || group.id}
                      onClick={() => handlePrincipalToggle(group.name)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#FFFFFF',
                        },
                        borderBottom: '1px solid #F0F0F0',
                      }}
                    >
                      <Checkbox
                        checked={formData.selectedPrincipals.includes(group.name)}
                        sx={{
                          color: '#E5E5E5',
                          '&.Mui-checked': { color: '#9B59B6' },
                        }}
                      />
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: '#9B59B6',
                          fontSize: '0.75rem',
                        }}
                      >
                        <GroupIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            color: '#253E66',
                          }}
                        >
                          {group.name}
                        </Typography>
                        {group.description && (
                          <Typography
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontSize: '0.75rem',
                              color: '#666666',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {group.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Paper>
            </Box>
            </motion.div>

            {/* Selected Summary */}
            {(formData.permissionSetIds.length > 0 || formData.selectedPrincipals.length > 0) && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: '#253E66',
                    mb: 1,
                  }}
                >
                  Selected:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {formData.permissionSetIds.map((id) => {
                    const ps = permissionSets.find((p) => p.id === id);
                    return (
                      <Chip
                        key={id}
                        label={ps?.name || id}
                        size="small"
                        onDelete={() => handlePermissionSetToggle(id)}
                        sx={{
                          backgroundColor: '#F0F9F4',
                          color: '#27AE60',
                          border: '1px solid #27AE6030',
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 500,
                          '& .MuiChip-deleteIcon': {
                            color: '#27AE60',
                            '&:hover': { color: '#1E8449' },
                          },
                        }}
                      />
                    );
                  })}
                  {formData.selectedPrincipals.map((name) => (
                    <Chip
                      key={name}
                      label={name}
                      size="small"
                      onDelete={() => handlePrincipalToggle(name)}
                      sx={{
                        backgroundColor: formData.principalType === 'USER' ? '#EBF5FB' : '#F5EBF8',
                        color: formData.principalType === 'USER' ? '#4698D3' : '#9B59B6',
                        border: `1px solid ${formData.principalType === 'USER' ? '#4698D330' : '#9B59B630'}`,
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                        '& .MuiChip-deleteIcon': {
                          color: formData.principalType === 'USER' ? '#4698D3' : '#9B59B6',
                          '&:hover': { color: formData.principalType === 'USER' ? '#3580B8' : '#8E44AD' },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Form Actions */}
            <motion.div variants={itemVariants}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                type="button"
                variant="outlined"
                onClick={onClose}
                disabled={loading}
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  borderColor: '#E5E5E5',
                  color: '#666666',
                  '&:hover': {
                    borderColor: '#4698D3',
                    color: '#4698D3',
                    backgroundColor: '#EBF5FB',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#E5E5E5',
                    color: '#999999',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || formData.selectedPrincipals.length === 0 || formData.permissionSetIds.length === 0}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} sx={{ color: 'white' }} />
                  ) : (
                    <PersonAddIcon />
                  )
                }
                sx={{
                  backgroundColor: '#4698D3',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#3580B8',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#E5E5E5',
                    color: '#999999',
                  },
                }}
              >
                {loading ? 'Assigning...' : 'Assign'}
              </Button>
            </Box>
            </motion.div>
          </Box>
        </Box>
      </motion.div>
    </Modal>
  );
};

export default AssignUsersGroupsModal;
